import React, { createContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Loading from './components/Loading/Loading';

const CHECK_SESSION_MUTATION = gql`
  mutation CheckSession($sessionId: String!) {
    checkSession(sessionId: $sessionId) {
      isValid
      coins
      success
      message
    }
  }
`;

const DELETE_SESSION_MUTATION = gql`
  mutation DeleteSession($sessionId: String!) {
    deleteSession(sessionId: $sessionId) {
      success
      message
    }
  }
`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [checkSession] = useMutation(CHECK_SESSION_MUTATION);
  const [deleteSession] = useMutation(DELETE_SESSION_MUTATION);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [coins, setCoins] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const performLogout = useCallback(async () => {
    if (sessionId) {
      try {
        await deleteSession({
          variables: { sessionId },
        });
      } catch (error) {
        console.error('Failed to delete session', error);
      }
    }
    Cookies.remove('sessionId');
    setIsAuthenticated(false);
    setSessionId(null);
    setCoins(0);
    window.location.href = '/';
  }, [sessionId, deleteSession]);

  const checkSessionValidity = useCallback(async (sid) => {
    try {
      const { data } = await checkSession({
        variables: { sessionId: sid },
      });

      if (data?.checkSession?.isValid) {
        setIsAuthenticated(true);
        setSessionId(sid);
        setCoins(data.checkSession.coins);
      } else {
        await performLogout();
      }
    } catch (error) {
      console.error('Session check failed', error);
      await performLogout();
    } finally {
      setIsLoading(false);
    }
  }, [checkSession, performLogout]);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedSessionId = Cookies.get('sessionId');
      if (storedSessionId) {
        await checkSessionValidity(storedSessionId);
      } else {
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [checkSessionValidity]);

  const authenticateUser = useCallback((newSessionId, newCoins) => {
    setSessionId(newSessionId);
    setIsAuthenticated(true);
    setCoins(newCoins);
    setIsLoading(false);
  }, []);

  // Usar el nuevo componente Loading cuando isLoading es true
  if (isLoading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        logout: performLogout, 
        authenticateUser, 
        coins,
        isLoading,
        sessionId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;